import {
  TreeData,
  TreeItemData,
  AltLabel,
  LeadingTypeIcon,
  TrailingVariantIcon,
} from '../TreeViewTypes';
import { DataNode, LabelContext } from '../data_nodes/types';
import {
  getAltLabelGenerator,
  getDefaultLabelGenerator,
  hasAltLabelGenerator,
  hasDefaultLabelGenerator,
} from '../label_generators/registry';

/**
 * Convert a DataNode to TreeData (top-level tree structure)
 */
export function convertToTreeData(
  dataNode: DataNode<any>,
  treeLabel: string,
  treeDescription?: string,
  treeId?: string,
): TreeData {
  // Convert the entire DataNode (including root) to a single TreeItemData
  // The root should be the milestone map that contains all other items
  const rootTreeItem = convertToTreeItemData(dataNode);

  // Generate unique tree ID based on milestone map ID and tree type
  const uniqueTreeId =
    treeId || `${dataNode.id}-tree-${treeLabel.toLowerCase().replace(/\s+/g, '-')}`;

  return {
    id: uniqueTreeId,
    label: treeLabel,
    description: treeDescription,
    treeItems: [rootTreeItem], // Single root item (milestone map)
    leadingTypeIcons: getLeadingTypeIcons(),
    trailingVariantIcons: getTrailingVariantIcons(),
  };
}

/**
 * Convert a DataNode to TreeItemData (individual tree node)
 */
export function convertToTreeItemData(
  dataNode: DataNode<any>,
  ancestors: DataNode<any>[] = [],
): TreeItemData {
  const context = buildLabelContext(dataNode, ancestors);

  // Generate default label
  const defaultLabel = generateDefaultLabel(dataNode, context);

  // Generate alt labels
  const altLabels = generateAltLabels(dataNode, context);

  // Convert children recursively
  const convertedChildren = dataNode.children.map((child) =>
    convertToTreeItemData(child, [...ancestors, dataNode]),
  );

  const treeItem: TreeItemData = {
    id: dataNode.id,
    label: defaultLabel,
    type: dataNode.type,
    children: convertedChildren.length > 0 ? convertedChildren : undefined,
    altLabels: altLabels.length > 0 ? altLabels : undefined,
    generalInfo: getGeneralInfo(dataNode.type),
    // TODO: Add action options and editable fields.
  };

  // Add metadata as properties if available
  if (dataNode.metadata) {
    Object.keys(dataNode.metadata).forEach((key) => {
      if (key === 'defaultExpanded' && dataNode.metadata![key] === true) {
        treeItem.defaultExpanded = true;
      }
      if (key === 'generalInfo' && typeof dataNode.metadata![key] === 'string') {
        treeItem.generalInfo = dataNode.metadata![key];
      }
      if (key === 'specificInfo' && typeof dataNode.metadata![key] === 'string') {
        treeItem.specificInfo = dataNode.metadata![key];
      }
      if (key === 'variant' && typeof dataNode.metadata![key] === 'string') {
        treeItem.variant = dataNode.metadata![key];
      }
      if (key === 'url' && typeof dataNode.metadata![key] === 'string') {
        treeItem.url = dataNode.metadata![key];
      }
    });
  }

  return treeItem;
}

/**
 * Build the label context for a tree node
 */
export function buildLabelContext(
  dataNode: DataNode<any>,
  ancestors: DataNode<any>[] = [],
): LabelContext {
  const root = ancestors.length > 0 ? ancestors[0] : dataNode;

  // Find siblings (children of the same parent)
  const parent = ancestors.length > 0 ? ancestors[ancestors.length - 1] : null;
  const siblings = parent ? parent.children.filter((child) => child.id !== dataNode.id) : [];

  return {
    allChildren: dataNode.children,
    ancestors,
    siblings,
    root,
    depth: ancestors.length,
  };
}

/**
 * Generate the default label for a tree node
 */
function generateDefaultLabel(dataNode: DataNode<any>, context: LabelContext): string {
  const generator = getDefaultLabelGenerator(dataNode.type);

  if (generator) {
    try {
      return generator(dataNode, context);
    } catch (error) {
      console.error(`Error generating default label for type ${dataNode.type}:`, error);
    }
  }

  // Fallback: try to extract a basic label from the data
  return generateFallbackDefaultLabel(dataNode);
}

/**
 * Generate alt labels for a tree node
 */
function generateAltLabels(dataNode: DataNode<any>, context: LabelContext): AltLabel[] {
  const generator = getAltLabelGenerator(dataNode.type);

  if (generator) {
    try {
      return generator(dataNode, context);
    } catch (error) {
      console.error(`Error generating alt labels for type ${dataNode.type}:`, error);
    }
  }

  // Return empty array if no generator found
  return [];
}

/**
 * Fallback default label generation when no generator is registered
 */
function generateFallbackDefaultLabel(dataNode: DataNode<any>): string {
  const data = dataNode.data;

  console.warn(
    '[generateFallbackDefaultLabel] Default label not found. Attempting to generate a fallback label.',
    dataNode.id,
    dataNode.type,
    dataNode.data,
  );

  // Try common properties that might contain display text
  if (data && typeof data === 'object') {
    if ('title' in data && typeof data.title === 'string') {
      return data.title;
    }
    if ('text' in data && typeof data.text === 'string') {
      return data.text;
    }
    if ('name' in data && typeof data.name === 'string') {
      return data.name;
    }
  }

  // Ultimate fallback
  return `${dataNode.type} (${dataNode.id})`;
}

/**
 * Utility function to check if conversion is ready for a tree
 */
export function isConversionReady(dataNode: DataNode<any>): {
  ready: boolean;
  missingGenerators: string[];
} {
  const missingGenerators: string[] = [];

  function checkNode(node: DataNode<any>): void {
    if (!hasDefaultLabelGenerator(node.type)) {
      missingGenerators.push(`Default generator for ${node.type}`);
    }

    node.children.forEach(checkNode);
  }

  checkNode(dataNode);

  return {
    ready: missingGenerators.length === 0,
    missingGenerators: Array.from(new Set(missingGenerators)),
  };
}

/**
 * Icon configurations (extracted from existing system)
 *
 * TODO: Revise icons.
 */
function getLeadingTypeIcons(): LeadingTypeIcon[] {
  return [
    { type: 'MilestoneMap', icon: 'folder' },
    { type: 'MajorObjective', icon: 'folder' },
    { type: 'Objective', icon: 'folder' },
    { type: 'StatusReportDate', icon: 'file' },
    { type: 'Status', icon: 'file' },
    { type: 'KeyResult', icon: 'file' },
    { type: 'Result', icon: 'file' },
    { type: 'Plan', icon: 'file' },
    { type: 'Owner', icon: 'file' },
    { type: 'AdditionalInfo', icon: 'file' },
  ];
}

/**
 * Icon configurations for our custom icon system
 * Maps type + variant combinations to specific icon identifiers
 */
function getTrailingVariantIcons(): TrailingVariantIcon[] {
  return [
    // === Set #1: Status Icons (Individual Items) ===
    { type: 'Status', variant: 'status-green', icon: 'status-green' },
    { type: 'Status', variant: 'status-yellow', icon: 'status-yellow' },
    { type: 'Status', variant: 'status-red', icon: 'status-red' },
    { type: 'Status', variant: 'status-planned', icon: 'status-planned' },
    { type: 'Status', variant: 'status-tbd', icon: 'status-tbd' },
    { type: 'Status', variant: 'status-none', icon: 'status-none' },

    { type: 'Objective', variant: 'status-green', icon: 'status-green' },
    { type: 'Objective', variant: 'status-yellow', icon: 'status-yellow' },
    { type: 'Objective', variant: 'status-red', icon: 'status-red' },
    { type: 'Objective', variant: 'status-planned', icon: 'status-planned' },
    { type: 'Objective', variant: 'status-tbd', icon: 'status-tbd' },
    { type: 'Objective', variant: 'status-none', icon: 'status-none' },

    { type: 'StatusReportDate', variant: 'status-green', icon: 'status-green' },
    { type: 'StatusReportDate', variant: 'status-yellow', icon: 'status-yellow' },
    { type: 'StatusReportDate', variant: 'status-red', icon: 'status-red' },
    { type: 'StatusReportDate', variant: 'status-planned', icon: 'status-planned' },
    { type: 'StatusReportDate', variant: 'status-tbd', icon: 'status-tbd' },
    { type: 'StatusReportDate', variant: 'status-none', icon: 'status-none' },

    // === Set #2: Summary Status Icons (Multi-Status Items) ===
    { type: 'MilestoneMap', variant: 'summary-green', icon: 'summary-green' },
    { type: 'MilestoneMap', variant: 'summary-yellow', icon: 'summary-yellow' },
    { type: 'MilestoneMap', variant: 'summary-red', icon: 'summary-red' },
    { type: 'MilestoneMap', variant: 'summary-green-yellow', icon: 'summary-green-yellow' },
    { type: 'MilestoneMap', variant: 'summary-green-red', icon: 'summary-green-red' },
    { type: 'MilestoneMap', variant: 'summary-yellow-red', icon: 'summary-yellow-red' },
    { type: 'MilestoneMap', variant: 'summary-green-yellow-red', icon: 'summary-green-yellow-red' },
    { type: 'MilestoneMap', variant: 'summary-planned', icon: 'summary-planned' },
    { type: 'MilestoneMap', variant: 'summary-tbd', icon: 'summary-tbd' },
    { type: 'MilestoneMap', variant: 'summary-planned-tbd', icon: 'summary-planned-tbd' },

    { type: 'MajorObjective', variant: 'summary-green', icon: 'summary-green' },
    { type: 'MajorObjective', variant: 'summary-yellow', icon: 'summary-yellow' },
    { type: 'MajorObjective', variant: 'summary-red', icon: 'summary-red' },
    { type: 'MajorObjective', variant: 'summary-green-yellow', icon: 'summary-green-yellow' },
    { type: 'MajorObjective', variant: 'summary-green-red', icon: 'summary-green-red' },
    { type: 'MajorObjective', variant: 'summary-yellow-red', icon: 'summary-yellow-red' },
    {
      type: 'MajorObjective',
      variant: 'summary-green-yellow-red',
      icon: 'summary-green-yellow-red',
    },
    { type: 'MajorObjective', variant: 'summary-planned', icon: 'summary-planned' },
    { type: 'MajorObjective', variant: 'summary-tbd', icon: 'summary-tbd' },
    { type: 'MajorObjective', variant: 'summary-planned-tbd', icon: 'summary-planned-tbd' },

    // === Set #3: Results Icons ===
    { type: 'Result', variant: 'results-pending', icon: 'results-pending' },
    { type: 'Result', variant: 'results-done', icon: 'results-done' },
    { type: 'Result', variant: 'results-not-done', icon: 'results-not-done' },

    { type: 'KeyResult', variant: 'results-pending', icon: 'results-pending' },
    { type: 'KeyResult', variant: 'results-done', icon: 'results-done' },
    { type: 'KeyResult', variant: 'results-not-done', icon: 'results-not-done' },

    /*
    // POC icons, maybe worth repurposing
    { type: 'KeyResult', variant: 'completed', icon: 'check-circle' },
    { type: 'KeyResult', variant: 'pending', icon: 'circle' },
    { type: 'Result', variant: 'completed', icon: 'check-circle' },
    { type: 'Result', variant: 'pending', icon: 'circle' },
    // */
  ];
}

/**
 * Get general info to display for everyone (but target audience is screen readers) on enter/action landing dialog/page
 *
 * TODO: Review/Revise
 */
function getGeneralInfo(type?: string): string {
  switch (type) {
    case 'MilestoneMap':
      return 'A milestone map contains all objectives and their progress over time';
    case 'MajorObjective':
      return 'A major objective that contains sub-objectives or detailed information';
    case 'Objective':
      return 'An objective represents a specific project or initiative';
    case 'StatusReportDate':
      return 'Status reporting date with associated status information';
    case 'Status':
      return 'Status information for an objective at a specific date';
    case 'KeyResult':
      return 'A key result represents a measurable outcome';
    case 'Result':
      return 'A result represents a measurable outcome';
    case 'Plan':
      return 'A plan or task related to achieving the objective';
    case 'Owner':
      return 'The person responsible for this objective';
    case 'AdditionalInfo':
      return 'Additional details about this objective';
    case 'Dependency':
      return '(A) below cannot be completed without or before (B).';
    default:
      return 'Tree item with additional information';
  }
}
